import _ from 'lodash';
import moment from 'moment';
import { TextStrings } from '../../Constants/TextStrings';
import { CalculationStepStatus } from '../Types/Calculation.types';
import { excelSheetContentType } from './constants';

export const saveStreamCSV = ( filename: string, blobPart: BlobPart): void => {
	saveStreamAsFile(excelSheetContentType, filename, blobPart);
};

export const saveStream = (blobType: string | null, filename: string, blobPart: BlobPart): void => {
	if (!!blobType) {
		const ext = getExtension(blobType);
		saveStreamAsFile(blobType, filename + ext, blobPart);
	}
};

const saveStreamAsFile = (blobType: string, filename: string, blobPart: BlobPart): void => {
	const anchor = document.createElement('a');
	anchor.download = filename;
	if (window.URL.createObjectURL) {
		// Everything else new.
		const blobObject = new Blob([blobPart], {
			type: blobType,
		});
		anchor.href = window.URL.createObjectURL(blobObject);
	}

	if (document.createEvent) {
		const event = document.createEvent('MouseEvents');
		event.initEvent('click', true, true);
		anchor.dispatchEvent(event);
	} else {
		anchor.click();
	}
};

export const getDisplayStatusName = (status: number) => {
	switch (status) {
		case 0:
			return TextStrings.PROCESSING;
		case 1:
		case 2:
		case 4:
		case 5:
		case 10:
			return TextStrings.FAILED;
		case 3:
			return TextStrings.SUCCESS;
		default:
			return '';
	}
};

export const getEnormousFileUploadStatusName = (status: number) => {
	switch (status) {
		case 0:
		case 1:
			return TextStrings.PROCESSING;
		case 2:
			return TextStrings.SUCCESS;
		case 3:
			return TextStrings.FAILED;
		default:
			return '';
	}
};

export const getDisplayMessageForCalculationStatus = (status: CalculationStepStatus) => {
	switch (status) {
		case CalculationStepStatus.Calculating:
		case CalculationStepStatus.NotStarted:
		case CalculationStepStatus.Enriching:
		case CalculationStepStatus.CancelEnabled:
		case CalculationStepStatus.ReadyForPackaging:
			return TextStrings.PROCESSING;
		case CalculationStepStatus.Failed:
		case CalculationStepStatus.PackagingFailed:
		case CalculationStepStatus.TimedOut:
			return TextStrings.FAILED;
		case CalculationStepStatus.Downloaded:
		case CalculationStepStatus.Complete:
			return TextStrings.SUCCESS;
		case CalculationStepStatus.Cancel:
			return TextStrings.CANCEL_CALCULATION;
		default:
			return '';
	}
};

export const checkCalculationInProgress = (status: CalculationStepStatus) => {
	switch (status) {
		case CalculationStepStatus.Calculating:
		case CalculationStepStatus.NotStarted:
		case CalculationStepStatus.Enriching:
		case CalculationStepStatus.CancelEnabled:
		case CalculationStepStatus.ReadyForPackaging:
			return true;
		default:
			return false;
	}
};

export const currDateAndTime = () => {
	function ConvertToDoubleDigits(num: string) {
		return num.padStart(2, '0');
	}

	//converting the Default mm//dd//yy format to yyyymmdd_hhmmss format
	const currTimeAndDate =
		new Date().getFullYear().toString() +
		ConvertToDoubleDigits((new Date().getMonth() + 1).toString()) +
		ConvertToDoubleDigits(new Date().getDate().toString()) +
		'_' +
		ConvertToDoubleDigits(new Date().getHours().toString()) +
		ConvertToDoubleDigits(new Date().getMinutes().toString()) +
		ConvertToDoubleDigits(new Date().getSeconds().toString());
	return currTimeAndDate;
};

export const getExtension = (blobType: string): string => {
	let extension = '';
	switch (blobType) {
		case 'application/zip':
			extension = '.zip';
			break;
		case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
			extension = '.xlsx';
			break;
		case 'text/csv':
			extension = '.csv';
			break;
		case 'application/json':
			extension = '.json';
			break;
		case 'application/vnd.ms-excel.sheet.macroEnabled.12':
			extension = '.xlsm';
			break;
		default:
			console.error(blobType + ' is invalid or not accepted.');
			break;
	}
	return extension;
};

export const sortByDescending = (array: any): any => {
	return _.orderBy(
		array,
		(o: any) => {
			return moment(o.ModifiedDate ?? o.ModifiedOn);
		},
		['desc'],
	);
};

export const sanitizeUserInput = (input: string): string => {
	return input.replace(new RegExp(/[^a-zA-Z0-9 ,-._()@]/g), '');
};

export const checkUndefinedOrNull = (input: any): boolean => {
	return (input === undefined || input === null);
};

export const getValue = (input: any): string => {
	if(input === undefined || input === null){
		return '';
	}
	return input;
};

export const humanizeCronExpression = (cronExpression: string): string => {
    if (cronExpression === '* * * * *') {
        return 'Every minute';
    }
    if (!cronExpression || cronExpression.trim() === '') {
        return 'No schedule configured';
    }
    if (cronExpression.trim() == '*****') {
        return 'Every minute of every hour of every day';
    }
    const parts = cronExpression.split(' ');
    if (parts.length !== 5 && parts.length !== 6) {
        return `Custom schedule: ${cronExpression}`;
    }
    let minute, hour, dayOfMonth, month, dayOfWeek;
    if (parts.length === 6) {
        [, minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    } else {
        [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    }
    const formatHour = (h: string, m: string = '0') => {
        const hourNum = parseInt(h);
        const minuteNum = parseInt(m || '0');
        const period = hourNum >= 12 ? 'PM' : 'AM';
        const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
        const displayMinute = minuteNum === 0 ? ':00' : ':' + m.padStart(2, '0');
        return `${displayHour}${displayMinute} ${period}`;
    };
    const getDayName = (day: string) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[parseInt(day)] || day;
    };
    const getMonthName = (monthNum: string) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months[parseInt(monthNum) - 1] || monthNum;
    };
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        if (hour !== '*') {
            const hourNum = parseInt(hour);
            if (isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
                return `Custom schedule: ${cronExpression}`;
            }
            return `Every day at ${formatHour(hour, minute)}`;
        } else if (hour === '*' && minute !== '*') {
            const minuteNum = parseInt(minute);
            if (isNaN(minuteNum) || minuteNum < 0 || minuteNum > 59) {
                return `Custom schedule: ${cronExpression}`;
            }
            return `Every hour at ${minute} minutes past the hour`;
        }
    }
    if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
        const dayNum = parseInt(dayOfWeek);
        if (isNaN(dayNum) || dayNum < 0 || dayNum > 6) {
            if (dayOfWeek.includes(',')) {
                const days = dayOfWeek.split(',').map(d => {
                    const dayNumber = parseInt(d.trim());
                    return (dayNumber >= 0 && dayNumber <= 6) ? getDayName(d.trim()) : null;
                }).filter(d => d !== null);
                if (days.length > 0 && hour !== '*') {
                    return `Every ${days.join(', ')} at ${formatHour(hour, minute)}`;
                }
            }
            return `Custom schedule: ${cronExpression}`;
        }
        const dayName = getDayName(dayOfWeek);
        if (hour !== '*') {
            const hourNum = parseInt(hour);
            if (isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
                return `Custom schedule: ${cronExpression}`;
            }
            return `Every ${dayName} at ${formatHour(hour, minute)}`;
        }
        return `Every ${dayName}`;
    }
    if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
        const day = parseInt(dayOfMonth);
        if (isNaN(day) || day < 1 || day > 31) {
            return `Custom schedule: ${cronExpression}`;
        }
        const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                      day === 2 || day === 22 ? 'nd' : 
                      day === 3 || day === 23 ? 'rd' : 'th';
        if (hour !== '*') {
            return `On the ${day}${suffix} of every month at ${formatHour(hour, minute)}`;
        }
        return `On the ${day}${suffix} of every month`;
    }
    if (dayOfMonth !== '*' && month !== '*' && dayOfWeek === '*') {
        const monthNum = parseInt(month);
        const day = parseInt(dayOfMonth);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12 || isNaN(day) || day < 1 || day > 31) {
            return `Custom schedule: ${cronExpression}`;
        }
        const monthName = getMonthName(month);
        const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                      day === 2 || day === 22 ? 'nd' : 
                      day === 3 || day === 23 ? 'rd' : 'th';
        if (hour !== '*') {
            return `Every year on ${monthName} ${day}${suffix} at ${formatHour(hour, minute)}`;
        }
        return `Every year on ${monthName} ${day}${suffix}`;
    }
    if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const minuteNum = parseInt(minute);
        if (isNaN(minuteNum) || minuteNum < 0 || minuteNum > 59) {
            return `Custom schedule: ${cronExpression}`;
        }
        return `Every hour at ${minute} minutes past the hour`;
    }
    return `Custom schedule: ${cronExpression}`;
};
